import { RvStatusType } from "views/rent-reporting/types";
import { useParams } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { FC, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant, Hide } from "shared/components";
import { getErrorMessage } from "utils/error";
import { SelectPayment } from "views/rent-reporting/components/SelectPayment";
import {
  AccountStatusVariant,
  PaymentStatuses,
  RvStatus
} from "views/rent-reporting/constants";
import { Verification } from "views/rent-reporting/types";
import { RvStatusBadge } from "views/rent-reporting/components/RvStatusBadge";
import { useAuthContext } from "providers/Auth";

const SelectAndReport: FC<{
  options: { value: string | number; label: JSX.Element }[];
  verificationId: number;
  subscriberId: string | undefined;
  isNoData?: boolean;
}> = ({ options, verificationId, subscriberId, isNoData }) => {
  const [selectValue, setSelectValue] = useState<string | number>(
    PaymentStatuses.PAID_ON_TIME
  );

  useEffect(() => {
    if (isNoData) setSelectValue(PaymentStatuses.NO_DATA);
  }, [isNoData]);

  const queryClient = useQueryClient();
  const { mutate: reportSingleRenter } = useMutation({
    mutationKey: ["REPORT_SINGLE_RENTER", subscriberId],
    mutationFn: (data: { verifications: Verification }) =>
      instance.patch(
        `/portal/v1/customers/${subscriberId}/verifications/${verificationId}`,
        { ...data.verifications }
      ),
    onSuccess: () => {
      toast.success("Renter has been successfully reported");
      queryClient.invalidateQueries({
        queryKey: ["SUBSCRIBER_DETAIL", subscriberId]
      });
      queryClient.invalidateQueries({
        queryKey: [
          "portal/v1/customers/all" + "__total",
          { page: 1, per_page: 1, type: AccountStatusVariant.NeedToReport }
        ]
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const handleReportClick = async () => {
    const getActionValue = () => {
      if (selectValue === PaymentStatuses.PAID_ON_TIME)
        return PaymentStatuses.PAID_ON_TIME;
      if (selectValue === PaymentStatuses.NO_DATA)
        return PaymentStatuses.NO_DATA;
      return PaymentStatuses.DELINQUENCY;
    };

    const verifications: Verification = {
      action: getActionValue(),
      months_delinquency:
        selectValue === PaymentStatuses.PAID_ON_TIME ||
        selectValue === PaymentStatuses.NO_DATA
          ? undefined
          : Number(selectValue)
    };
    reportSingleRenter({ verifications });
  };

  return (
    <div className="flex gap-4 items-center">
      <SelectPayment
        id={String(subscriberId)}
        value={selectValue || PaymentStatuses.PAID_ON_TIME}
        handleChange={(e) => setSelectValue(e.target.value)}
        options={options}
      />
      <Hide if={isNoData && selectValue === PaymentStatuses.NO_DATA}>
        <Button
          variant={isNoData ? ButtonVariant.BASE : ButtonVariant.PRIMARY}
          borderLess={Boolean(isNoData)}
          size={ButtonSize.XS}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleReportClick();
          }}
        >
          {isNoData ? "Update" : "Report"}
        </Button>
      </Hide>
    </div>
  );
};

export const PaymentStatusElement = ({
  status,
  options,
  verificationId
}: {
  status: RvStatusType;
  options: { value: string | number; label: JSX.Element }[];
  verificationId: number;
}) => {
  const { renterId } = useParams();
  const { permissions } = useAuthContext();

  if (
    (status.status === RvStatus.PendingVerification ||
      status.status === RvStatus.ManualVerificationNeeded) &&
    permissions.boomReportWriteAccess
  )
    return (
      <SelectAndReport
        verificationId={verificationId}
        subscriberId={renterId}
        options={options}
        isNoData={status.status === RvStatus.ManualVerificationNeeded}
      />
    );

  return <RvStatusBadge status={status} />;
};
